import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { trackOnboardingButtonClick, trackOnboardingButtonShow } from './tracking';
import { setEditableMessage } from '../../actions/chat/actions';
import { ChatbotOnboardingButtonsLayout } from '../../components/ChatbotOnboardingButtonsLayout';
import { OnboardingButton } from '../../components/OnboardingButton';
import { CHATBOT_ID } from '../../constants/chatbotId';
import { chatbotSelector, currentOpenChatIDSelector } from '../../selectors';
import { EChatbotActionType } from '../../services/fetchChatbotActionButtons';
import { IChatbotOnboardingButtonSchema } from '../../types/chatbot';
import { useApplicationContext } from '../../utils/applicationContext';

type TOnboardingButtonContainerProps = {};

export const OnboardingButtonContainer: React.FC<TOnboardingButtonContainerProps> = () => {
  const { config } = useApplicationContext();
  const dispatch = useDispatch();
  const { onboardingButtons } = useSelector(chatbotSelector);

  const prevOnboardingButtonsLength = React.useRef(onboardingButtons.length);

  const isChatbotGPTEnabled = config.get<boolean>('frontend-chats.chatbotGPT.Enabled') || false;
  const chatId = useSelector(currentOpenChatIDSelector);

  const onClick = React.useCallback(
    (onboardingButtonChatbot: IChatbotOnboardingButtonSchema) => {
      const { analyticsType, actionType, inputText } = onboardingButtonChatbot;

      trackOnboardingButtonClick(analyticsType);
      if (actionType === EChatbotActionType.CopyToInput && inputText) {
        dispatch(setEditableMessage({ chatId: CHATBOT_ID, text: inputText }));

        return;
      }
    },
    [dispatch],
  );

  const getAnalyticsType = (buttons: IChatbotOnboardingButtonSchema[]) => {
    return buttons.map(({ analyticsType }) => analyticsType);
  };

  React.useEffect(() => {
    if (onboardingButtons.length !== 0) {
      trackOnboardingButtonShow(getAnalyticsType(onboardingButtons));
    }
  }, []);

  React.useEffect(() => {
    if (onboardingButtons.length !== 0 && onboardingButtons.length !== prevOnboardingButtonsLength.current) {
      trackOnboardingButtonShow(getAnalyticsType(onboardingButtons));
    }
    prevOnboardingButtonsLength.current = onboardingButtons.length;
  }, [onboardingButtons]);

  const buttons =
    chatId === CHATBOT_ID &&
    isChatbotGPTEnabled &&
    onboardingButtons &&
    onboardingButtons.map((onboardingButtonChatbot, index) => {
      return <OnboardingButton key={index} onboardingButton={onboardingButtonChatbot} onClick={onClick} />;
    });

  if (!buttons || !buttons.length) {
    return null;
  }

  return <ChatbotOnboardingButtonsLayout>{buttons}</ChatbotOnboardingButtonsLayout>;
};
